<template>
  <svg
    v-if="name"
    class="mf-icon"
    :class="`mf-icon--${size}`"
    viewBox="0 -960 960 960"
    :role="exposed ? 'img' : undefined"
    :aria-hidden="exposed ? undefined : 'true'"
    :aria-label="exposed ? accessibleLabel : undefined"
    focusable="false"
  >
    <path v-if="path" :d="path" fill="currentColor" />
  </svg>
</template>

<script>
import { ICON_SIZES, RESOLVABLE_ICON_NAMES, resolveIcon } from './icons'

const isDev = typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production'

/**
 * Icon (`_Icons set`): átomo visual fundacional, consumido por instância dentro de outros
 * componentes (Button, Checkbox, Alert, Chip, IconButton…). É STATELESS e não tem cor própria:
 * herda a cor do consumidor via `currentColor` (Icon Color Same Reference Rule). O tamanho é a
 * variante `Size` (xs/sm/md/lg/xl); consumidores NÃO criam `[componente]/icon/size`.
 * Catálogo fechado de 114 nomes em 8 categorias em ./icons.js (espelho da variante `Name` do node
 * 429:1135; o Outline v1.1.0 ainda diz 110, drift aberto). Tokens de tamanho em gondola.tokens.css.
 *
 * A11y: decorativo por padrão (`aria-hidden`). Quando o ícone é o único conteúdo de um controle
 * interativo, o label acessível pertence ao PAI (ex.: IconButton `aria-label`), não ao Icon.
 * Ícone `-filled` que comunica estado togglable exige `aria-pressed`/`aria-checked` no pai.
 *
 * DIVERGÊNCIA ABERTA com o Outline "Icon" v1.1.0, seção Accessibility: o doc é absoluto ("Icon is a
 * non-interactive visual element. It carries no independent ARIA role" e o label "belongs to the
 * parent control via aria-label, never to the icon itself"), e este componente oferece a saída
 * `decorative=false` + `label`, que aplica `role="img"` no próprio ícone. A saída NÃO é teórica: o
 * produto já a usa em 3 lugares onde não existe controle pai para carregar o nome (MapaInfoCard.vue:6
 * e :23, Mapa.vue:40, todos ícone solto de ajuda/aviso ao lado de texto). Enquanto o design lead não
 * decidir, o default segue o doc (aria-hidden) e a saída fica opt-in. Sem `label` o componente NÃO
 * cai para o `name`: um token kebab-case em inglês ("check-circle") lido numa UI pt-BR é pior que
 * silêncio, e para um alias legado ele anunciaria o nome deprecado.
 *
 * CAIXA: o svg é renderizado sempre que o consumidor pediu um ícone (`name` preenchido), mesmo com
 * nome que não resolve. Em build de produção o validator da prop não roda e o aviso de dev não
 * existe, então colapsar o elemento tiraria a área do glifo do layout (shift). Já sem `name` (bind
 * dinâmico ainda vazio) nada é renderizado, para não injetar caixa fantasma em `:name="item.icon"`.
 * O componente tem raiz única: comentário no template viraria segundo nó raiz e mataria a herança
 * de classe/atributo do consumidor.
 */
export default {
  name: 'MfIcon',
  props: {
    /**
     * Nome do ícone. Um dos 114 de ICON_NAMES (variante `Name` do Figma). O validator também aceita
     * os nomes legados de ICON_ALIASES e o fora-de-catálogo `sort`, que resolvem com aviso: o gate
     * é o catálogo fechado, não a limpeza do débito.
     */
    name: {
      type: String,
      required: true,
      validator: (v) => RESOLVABLE_ICON_NAMES.includes(v),
    },
    /** Tamanho (variante Size). */
    size: {
      type: String,
      default: 'md',
      validator: (v) => ICON_SIZES.includes(v),
    },
    /** Decorativo (aria-hidden). Defina `false` + `label` quando o ícone carrega significado. */
    decorative: {
      type: Boolean,
      default: true,
    },
    /** Rótulo acessível, em pt-BR, quando `decorative=false`. Sem ele o ícone segue `aria-hidden`. */
    label: {
      type: String,
      default: '',
    },
  },
  computed: {
    resolved() {
      const found = resolveIcon(this.name)

      if (isDev) {
        if (!found) {
          console.warn(
            `[MfIcon] "${this.name}" não existe no catálogo do Gôndola (_Icons set, 114 nomes): a caixa do tamanho é reservada, sem glifo. Use um nome de ICON_NAMES, ou adicione o ícone ao Figma primeiro.`,
          )
        } else if (found.status === 'alias') {
          console.warn(`[MfIcon] "${this.name}" é um nome legado e vai sair: use "${found.canonical}", o nome canônico da variante Name no Figma.`)
        } else if (found.status === 'off-catalog') {
          console.warn(
            `[MfIcon] "${this.name}" não faz parte do _Icons set do Figma (fora do catálogo). Precisa entrar no Figma ou ser trocado por um ícone do catálogo.`,
          )
        }
      }

      return found
    },
    path() {
      return this.resolved ? this.resolved.path : null
    },
    /** Rótulo acessível efetivo. Nunca cai para `name` (ver DIVERGÊNCIA ABERTA no docblock). */
    accessibleLabel() {
      if (this.decorative) return ''

      if (!this.label && isDev) {
        console.warn(
          `[MfIcon] "${this.name}" com decorative=false e sem label: segue aria-hidden. Passe um label em pt-BR, ou deixe o rótulo no controle pai (Outline "Icon", Accessibility).`,
        )
      }

      return this.label
    },
    /** Exposto ao leitor de tela: o consumidor pediu, deu um rótulo, e existe glifo para descrever. */
    exposed() {
      return Boolean(this.accessibleLabel) && Boolean(this.path)
    },
  },
}
</script>

<style scoped>
.mf-icon {
  display: inline-block;
  flex: 0 0 auto;
  fill: currentColor;
  vertical-align: middle;
}
.mf-icon--xs {
  width: var(--mf-icon-size-xs);
  height: var(--mf-icon-size-xs);
}
.mf-icon--sm {
  width: var(--mf-icon-size-sm);
  height: var(--mf-icon-size-sm);
}
.mf-icon--md {
  width: var(--mf-icon-size-md);
  height: var(--mf-icon-size-md);
}
.mf-icon--lg {
  width: var(--mf-icon-size-lg);
  height: var(--mf-icon-size-lg);
}
.mf-icon--xl {
  width: var(--mf-icon-size-xl);
  height: var(--mf-icon-size-xl);
}
</style>
